import * as React from 'react';
import { LanguageContext } from '../utilities/LocalizationModule';
import TopNavigator from "../components/TopNavigator"
import { DefaultButton, Stack } from "@fluentui/react"
import UsersTable from '../components/displays/UsersTable';
import { Routes, Route, Navigate } from "react-router-dom";
import { connect, ConnectedProps } from 'react-redux';
import { IPortalUser, IUserProfileRequest } from '../data-structures/interfaces';
import { getCurrentProfile, setAuthToken } from '../utilities/helpers/ApiHelper';
import { setUserProfile } from "../redux/modules/user"
import { getRequests } from "../redux/modules/requests"
import { getDatasourceTypes } from "../redux/modules/datasource"
import Dashboard from '../components/displays/Dashboard';
import Subscriptions from '../components/displays/Subsriptions';
import MessageScreen from "./MessageScreen"
import NavMenu from "../components/NavMenu"
import { MainViewStyle } from "../styles/HomeStyles"
import { useMsal } from '@azure/msal-react';
import Loading from '../components/Loading';
import { RootState } from '../redux';
import PartnersOverview from "../components/displays/PartnersOverview";
import Requests from "../components/displays/Requests";
import { AuthHelper } from '../utilities/helpers/AuthHelper';

const mapDispatchToProps = {
    setUserProfile,
    getRequests,
    getDatasourceTypes
};

const mapStateToProps = (state: RootState) => {
    return {
        requests: state.requests.requests,
        isGlobalAdmin: state.user.isGlobalAdmin
    }
}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
interface HomeProps extends PropsFromRedux { }

const Home: React.FC<HomeProps> = ({
    setUserProfile,
    requests,
    getRequests,
    getDatasourceTypes,
    isGlobalAdmin
}: HomeProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [isUnauthorized, setIsUnauthorized] = React.useState<boolean>(false);
    const [userTenantName, setUserTenantName] = React.useState<string>("");

    const { instance, accounts } = useMsal();

    React.useEffect(() => {
        updateData();
    }, []);

    const updateData = async () => {
        setIsLoading(true);
        try {
            AuthHelper.setSignOutCallback(signOut);
            const profileRequest: IUserProfileRequest = { accountId: accounts[0]?.homeAccountId, username: accounts[0]?.username, clientId: accounts[0]?.idTokenClaims?.aud };
            if (await setAuthToken(profileRequest)) {
                let userProfile: IPortalUser = await getCurrentProfile(profileRequest);
                if (userProfile) {
                    let userTenantName: string;
                    userProfile.tenants = userProfile.tenants.map(tenant => {

                        if (!userTenantName && tenant.tenantId === userProfile.tenantId && tenant.friendlyName) {
                            userTenantName = tenant.friendlyName.charAt(0).toUpperCase() + tenant.friendlyName.slice(1);
                            setUserTenantName(userTenantName);
                        }

                        const advancedFields = (tenant.allowedAdvancedSearchFields as string)?.trim().toLowerCase().split(";") ?? [];
                        const allowedTeams = (tenant.allowedTeams as string)?.trim().toLowerCase().split(";") ?? [];
                        const allowedSiteCollections = (tenant.allowedSiteCollections as string)?.trim().toLowerCase().split(";") ?? [];
                        const hiddenMetadataFields = (tenant.hiddenMetadataFields as string)?.trim().toLowerCase().split(";") ?? [];
                        return ({
                            ...tenant,
                            allowedAdvancedSearchFields: advancedFields,
                            allowedSiteCollections,
                            allowedTeams,
                            hiddenMetadataFields,
                        });
                    });
                    setUserProfile(userProfile, accounts[0]);
                    if (requests.length === 0 && userProfile.isGlobalAdmin) getRequests();
                    getDatasourceTypes();
                }
                else {
                    setIsUnauthorized(true);
                }
            }
            else {
                signOut();
            }
        } catch (e) {
            console.error(e);
        }

        setIsLoading(false);
    };

    const signOut = () => {
        instance.logoutRedirect();
    }

    return isLoading
        ? <Loading label={languageStrings.LoadingPortal} />
        : !isUnauthorized
            ? <React.Fragment>
                <TopNavigator userTenantName={userTenantName} signOut={signOut} />
                <Stack horizontal>
                    <Stack.Item>
                        <NavMenu />
                    </Stack.Item>
                    <Stack.Item className={MainViewStyle}>
                        <Routes>
                            <Route index element={<Navigate to="/dashboard" replace />} />
                            <Route path="dashboard" element={<Dashboard updateData={updateData} />} />
                            <Route path="users" element={<UsersTable />} />
                            {/* Partners views are admin-only: register the routes only for
                                global admins so non-admins can't reach them by direct URL.
                                Non-admins fall through to the catch-all -> /dashboard. */}
                            {isGlobalAdmin && <Route path="partners/overview" element={<PartnersOverview />} />}
                            {isGlobalAdmin && <Route path="partners/requests" element={<Requests />} />}
                            {/* Single optional-param route: /subscriptions and /subscriptions/:id
                                share ONE <Subscriptions/> instance, so toggling between them
                                re-scopes the live SignalR connection instead of remounting it. */}
                            <Route path="subscriptions/:tenantId?" element={<Subscriptions />} />
                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                    </Stack.Item>
                </Stack>
            </React.Fragment>
            : <MessageScreen
                text={languageStrings.UnauthorizedMessage}
                actionButton={
                    <DefaultButton onClick={signOut}>
                        {languageStrings.SignInDifferentAccount}
                    </DefaultButton>
                }
            />
}

export default connector(Home);